// @flow

import { observable, action, runInAction, computed } from 'mobx';
import invariant from 'invariant';

import ApiManufacturers from './api/manufacturers';
import ApiCategories from './api/categories';
import ApiAdConditions from './api/ad-conditions';
import ApiAdvertisers from './api/advertisers';
import ApiCountries from './api/countries';

import type { Store } from '@insolus/stores';
import type { Option } from '@insolus/ui-kit/components/Select';
import type { Category } from 'stores/api/categories';
import type { Advertiser } from 'stores/api/advertisers';

export type FetchOptions = {|
    force: boolean
|};

export type Entity = {|
    id: any,
    name: string
|};

const ENTITIES_MAP = {
    manufacturers : ApiManufacturers,
    categories    : ApiCategories,
    adConditions  : ApiAdConditions,
    advertisers   : ApiAdvertisers,
    countries     : ApiCountries
};

// eslint-disable-next-line flowtype/require-types-at-top
type EntitiesType = {
    [entity: $Keys<typeof ENTITIES_MAP>]: {|
        fetched: boolean,
        data: any[]
    |}
};

const _formatOptions = (entities: Entity[]): Option[] => (
    entities.map((entity: Entity) => ({
        label : entity.name,
        value : entity.id
    }))
);

const initEntities = (): EntitiesType => (
    Object.keys(ENTITIES_MAP).reduce((acc: EntitiesType, name: string): EntitiesType => {
        const config = { fetched: false, data: [] };
        return { ...acc, [name]: config };
    }, {})
);

class EntitiesStore implements Store {
    @observable entities: EntitiesType = initEntities();

    @computed
    get optionAdvertisers(): Option[] | null {
        const { data, fetched } = this.entities.advertisers;
        if (!fetched) {
            return null;
        }

        return data.map((entity: Advertiser): Option => {
            const label = entity.displayName
                ? `${entity.displayName} (${entity.email})`
                : entity.email;

            return {
                label,
                value: entity.id
            };
        });
    }

    @computed
    get optionManufacturers(): Option[] | null {
        const { data, fetched } = this.entities.manufacturers;
        return fetched ? _formatOptions(data) : null;
    }

    @computed
    get optionCountries(): Option[] | null {
        const { data, fetched } = this.entities.countries;
        return fetched ? _formatOptions(data) : null;
    }

    @computed
    get optionCategories(): Option[] | null {
        const { data, fetched } = this.entities.categories;
        if (!fetched) {
            return null;
        }

        return data.map((category: Category): Option => ({
            label : `${'\u00a0'.repeat(category.level * 6)}└─ ${category.name}`,
            value : category.id
        }));
    }

    @computed
    get optionAdConditions(): Option[] | null {
        const { data, fetched } = this.entities.adConditions;
        return fetched ? _formatOptions(data) : null;
    }

    @computed
    // eslint-disable-next-line class-methods-use-this-regex/class-methods-use-this
    get optionCurrencies(): Option[] {
        return [
            { label: 'CHF', value: 'CHF' },
            { label: 'EUR', value: 'EUR' }
        ];
    }

    @action
    async fetch(name: $Keys<typeof ENTITIES_MAP>, options: $Shape<FetchOptions> = {}) {
        invariant(
            name in ENTITIES_MAP && name in this.entities,
            `L'entité "${name}" n'est pas reconnue par l'\`EntitiesStore\`.`
        );

        const _options = { force: false, ...options };

        // - Already fetched and not forced.
        if (this.entities[name].fetched && !_options.force) {
            return;
        }

        const agent = ENTITIES_MAP[name];
        try {
            const data: any = await agent.all();
            runInAction(() => {
                this.entities[name].fetched = true;
                this.entities[name].data = data;
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error:', error.message || error.code);
        }
    }

    @action
    refresh() {
        Object.keys(ENTITIES_MAP).forEach((name: $Keys<typeof ENTITIES_MAP>) => {
            if (!this.entities[name].fetched) {
                return;
            }
            this.fetch(name, { force: true });
        });
    }

    @action
    get(name: $Keys<typeof ENTITIES_MAP>, id: any): Entity | null {
        invariant(
            name in ENTITIES_MAP && name in this.entities,
            `L'entité "${name}" n'est pas reconnue par l'\`EntitieStore\`.`
        );

        if (!this.entities[name].fetched) {
            return null;
        }

        return this.entities[name].data.find(({ id: _id }) => _id === id) || null;
    }

    optionDisabledCategories(excludeCategory: Category): Option[] | null {
        if (!this.optionCategories) {
            return null;
        }

        const categoryDisabled = [];
        const { data } = this.entities.categories;

        let _category;
        for (let i = 0; i < data.length; i += 1) {
            _category = data[i];

            if (_category.id === excludeCategory.id) {
                categoryDisabled.push(_category.id);
            }

            if (categoryDisabled.length) {
                if (_category.level > excludeCategory.level) {
                    categoryDisabled.push(_category.id);
                }

                if (_category.id !== excludeCategory.id &&
                    _category.level === excludeCategory.level
                ) {
                    break;
                }
            }
        }

        return this.optionCategories?.map((option: Option): Option => ({
            ...option,
            disabled: categoryDisabled.includes(option.value)
        }));
    }
}

export default (new EntitiesStore(): EntitiesStore);
export { EntitiesStore };
